/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useEffect, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type GoalProgress } from '../../lib/api/progress.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useChildProgress } from '../../lib/queries/progress.js';
import { formatIst } from '../../lib/utils/datetime.js';

import { ChildSelect } from './child-select.js';

/** Therapy-plan goal card (Figma): category pill + target date, title, bar, "% complete". */
function GoalProgressCard({ goal }: { readonly goal: GoalProgress }): JSX.Element {
  const { t } = useTranslation();
  const pct = goal.points.at(-1)?.accuracyPct ?? 0;
  const onTrack = pct >= 50;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-2 flex items-center justify-between gap-3">
        {goal.category !== null ? (
          <span className="bg-[var(--accent)]/10 rounded-full px-3 py-1 text-xs font-semibold capitalize text-[var(--accent)]">
            {goal.category}
          </span>
        ) : (
          <span />
        )}
        {goal.targetDate !== null && (
          <span className="text-xs text-slate-400">
            {t('parent.therapyPlan.target', { date: formatIst(goal.targetDate, 'MMM yyyy') })}
          </span>
        )}
      </div>
      <p className="font-semibold text-slate-800 dark:text-white">{goal.title}</p>
      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className={`h-full rounded-full ${onTrack ? 'bg-emerald-500' : 'bg-orange-500'}`}
          style={{ width: `${String(pct)}%` }}
        />
      </div>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        {t('parent.progress.percentComplete', { pct })}
      </p>
    </div>
  );
}

/** Parent → Therapy Plan (Figma). Stacked goal cards: category · target · progress. */
export function ParentProgressPage(): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const [childId, setChildId] = useState('');

  const children = useMemo(() => clients.data ?? [], [clients.data]);
  useEffect(() => {
    if (childId === '' && children.length > 0) {
      setChildId(children[0]?.id ?? '');
    }
  }, [children, childId]);

  const progress = useChildProgress(childId, childId !== '');
  const goals = progress.data ?? [];

  return (
    <div className="p-8">
      <PageHeader
        title={t('parent.therapyPlan.title')}
        subtitle={t('parent.therapyPlan.subtitle')}
      />
      {children.length > 1 && (
        <div className="mt-4">
          <ChildSelect clients={children} value={childId} onSelect={setChildId} />
        </div>
      )}

      <QueryState
        isLoading={clients.isLoading || progress.isLoading}
        isError={clients.isError || progress.isError}
        isEmpty={goals.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('parent.progress.empty')}
      >
        <div className="mt-4 space-y-3">
          {goals.map((goal) => (
            <GoalProgressCard key={goal.goalId} goal={goal} />
          ))}
        </div>
      </QueryState>
    </div>
  );
}
